import { ceil, last } from 'lodash'
import { DateTime } from 'luxon'
import { computed, type MaybeRefOrGetter, toRef, toValue, watch } from 'vue'
import type { Service, ServiceLevel } from '../../Types/service'

export default function useService(existingService: MaybeRefOrGetter<Service | null> = null) {
  const service = toRef(existingService)
  const onlyInitialLevel = computed<boolean>(() => toValue(service)?.levels.length === 1)
  const lastServiceLevel = computed<ServiceLevel | null>(() => last(toValue(service)?.levels ?? []) ?? null)

  const realStartAt = computed<DateTime | null>(() => {
    const startAt = service.value?.startAt ?? service.value?.contractStartAt
    if (!startAt) {
      return null
    }

    return DateTime.fromISO(startAt)
  })

  const realEndAt = computed<DateTime | null>(() => {
    const endAt = service.value?.endAt ?? service.value?.contractEndAt
    if (!endAt) {
      return null
    }

    return DateTime.fromISO(endAt)
  })

  const billingPeriodicityInMonths = computed<number>(() => service.value?.billingPeriod?.duration ?? 1)

  const totalTerms = computed<number>(() => {
    if (!realStartAt.value || !realEndAt.value) {
      return service.value?.totalTerms ?? 0
    }

    const durationInMonths = realEndAt.value.diff(realStartAt.value.plus({ day: 1 }), 'month').months
    return Math.max(0, ceil(durationInMonths / billingPeriodicityInMonths.value))
  })

  const levelTerms = computed<number>(() => {
    const levels = service.value?.levels
    if (!Array.isArray(levels)) {
      return 0
    }

    return levels.reduce((total, { terms }) => total + Number(terms), 0)
  })

  const hasMissingTerms = computed<boolean>(() => {
    return levelTerms.value !== totalTerms.value
  })

  const hasStartDate = computed<boolean>(() => !!service.value?.startAt || !!service.value?.contractStartAt)

  const isDeletable = computed<boolean>(() => {
    if (null === service.value) {
      return false
    }

    const serviceType = service.value?.serviceType
    const serviceCategory = serviceType?.serviceCategory

    return !serviceCategory?.mandatory || !serviceType?.exclusive
  })

  function updateServiceAmount(): void {
    if (service.value === null) {
      return
    }

    if (service.value.level === true) {
      service.value.amount = null
    } else {
      const levels = service.value?.levels ?? []
      service.value.amount ??= levels[0]?.amount ?? 0
    }
  }

  function updateLevelTerms(): void {
    if (service.value === null || !Array.isArray(service.value?.levels)) {
      return
    }

    const initialDate: DateTime | null = toValue(realStartAt.value)
    let remainingTerms = totalTerms.value
    let elapsedTerms = 0
    for (let level of service.value.levels) {
      const terms = Math.min(remainingTerms, Number(level.terms))
      let startAt: DateTime | null = null
      if (terms > 0) {
        startAt = initialDate?.plus({ month: elapsedTerms * billingPeriodicityInMonths.value }) ?? null
      }

      level.startAt = startAt?.toISO() ?? null
      level.terms = terms

      elapsedTerms += terms
      remainingTerms = Math.max(0, remainingTerms - terms)

      if (lastServiceLevel.value?.position === level.position && remainingTerms > 0) {
        level.terms = Number(terms) + remainingTerms
      }
    }
  }

  function addNewLevel() {
    if (service.value === null) {
      return
    }

    const level = {
      position: 1 + (lastServiceLevel.value?.position ?? 0),
      terms: 0,
      amount: lastServiceLevel.value?.amount ?? 0,
      startAt: null,
    }

    service.value.levels.push(level)
  }

  function removeLevel(indexToRemove: number) {
    if (service.value === null) {
      return
    }

    const removedLevel = service.value.levels[indexToRemove]
    if (!removedLevel) {
      return
    }

    service.value.levels = service.value.levels.filter(({ position }) => position !== removedLevel.position)
    for (let level of service.value.levels) {
      if (level.position < removedLevel.position) {
        continue
      }

      level.position -= 1
    }
  }

  watch(
    [
      () => toValue(service)?.startAt,
      () => toValue(service)?.endAt,
      () => toValue(service)?.levels?.map(({ terms }) => Number(terms)),
    ],
    updateLevelTerms,
    { deep: true },
  )

  watch(() => toValue(service)?.level, updateServiceAmount)

  return {
    // Ref
    service,
    // Computed
    totalTerms,
    levelTerms,
    hasStartDate,
    hasMissingTerms,
    onlyInitialLevel,
    lastServiceLevel,
    isDeletable,
    // Methods
    addNewLevel,
    removeLevel,
  }
}
